import { useState, useMemo, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { onReady, trackEvent, updateAppStatusBar } from '../../utils/appBridge'
import { throttle } from '../../utils/utils'
import { motion } from 'framer-motion'
import { EliteLandingPageState, EliteOnboardingStatusResponse, EventNames } from '../../types/misc.types'
import { fetchOnboardingAdvisoryStatus } from '../../repository/misc'
import { useQuery } from '@tanstack/react-query'
import { smartRebalance } from '../../repository/rebalance'
import { EliteLandingPageLoader } from '../../components/elite/landing-page/EliteLandingPageLoader'
import { EliteButtonV3 } from '../../components/elite/EliteButtonV3'
import { getEliteConsentScrollSpacerClass, hasEliteConsentText } from '../../components/elite/EliteConsentTextContent'
import { getBottomPadding } from '../../utils/sessionStorage'
import { BrokeEliteLandingPageHead } from '../../components/elite/landing-page/versions/landing-page-head/BrokeEliteLandingPageHead'
import { BrokeEliteLandingPageBody } from '../../components/elite/landing-page/versions/landing-page-body/BrokeEliteLandingPageBody'
import { eliteLandingPageStateConfig } from '../../utils/eliteLandingPageConfig'
import { ElitePageBackBar } from '../../components/elite/ElitePageBackBar'
import { resolveEliteLandingOfferFromStatus } from '../../components/elite/EliteSpecialOfferGiftBanner'
import { getEliteBuyCtaEventExtras } from '../../utils/eliteEventExtras'
import { EliteBuyCtaExtrasProvider } from '../../components/elite/EliteBuyCtaExtrasContext'
import { EliteAboutVideoFab } from '../../components/elite/EliteAboutVideoFab'
import { useIsClearOfBottomFabZone } from '../../components/elite/useIsClearOfBottomFabZone'

const ELITE_LANDING_PAGE_VERSION = 'ELITE_LANDING_PAGE_1L'


export const BrokeLandingPage = () => {
  const [isEliteButtonVisible, setIsEliteButtonVisible] = useState(false)
  const [isEliteButtonDisabled, setIsEliteButtonDisabled] = useState(false)
  const [eliteOnboardingData, setEliteOnboardingData] = useState<EliteOnboardingStatusResponse>()
  const [isConsentChecked, setIsConsentChecked] = useState(false)
  const [isNotified, setIsNotified] = useState(false)
  const onReadyLock = useRef<boolean>(false)
  const floatingEliteCtaRef = useRef<HTMLDivElement>(null)
  const inlineEliteCtaRef = useRef<HTMLDivElement>(null)
  const scrollRootRef = useRef<HTMLDivElement>(null)

  const throttledEliteButtonVisibility = throttle((eliteButtonVisibility: boolean) => {
    setIsEliteButtonVisible(eliteButtonVisibility)
  }, 0)
  const handleEliteButtonVisibility = (eliteButtonVisibility: boolean) =>
    throttledEliteButtonVisibility(eliteButtonVisibility)
  const handleIsNotified = () => setIsNotified(true)

  const [searchParams] = useSearchParams()

  const isWhitelisted = useMemo(() => {
    return searchParams?.get('isWhitelisted') ?? 'false'
  }, [searchParams])

  const { data, isSuccess, isLoading, isError } = useQuery<any, Error>({
    queryKey: ['ONBOARDING_ADVISORY_STATUS'],
    queryFn: fetchOnboardingAdvisoryStatus,
    retry: 1,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true
  })

  const {
    data: smartRebalanceData,
    isSuccess: isSmartRebalanceSuccess,
    isError: isSmartRebalanceError
  } = useQuery<any, Error>({
    queryKey: ['SMART_REBALANCE'],
    queryFn: smartRebalance,
    retry: 1,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true
  })
  const showAboutVideoFab = useIsClearOfBottomFabZone(inlineEliteCtaRef, scrollRootRef, !isLoading)

  useEffect(() => {
    if (isSuccess) {
      const dataCopy = data?.data as EliteOnboardingStatusResponse
      setEliteOnboardingData(() => dataCopy)
    }
  }, [isSuccess])

  const handlePageReady = () => {
    if (!onReadyLock.current) {
      onReadyLock.current = true
      onReady()
    }
  }

  useEffect(() => {
    updateAppStatusBar('000000', 'light')
  }, [])

  useEffect(() => {
    if (isSmartRebalanceSuccess || isSmartRebalanceError || isSuccess || isError)
      setTimeout(() => handlePageReady(), 150)
  }, [isSmartRebalanceSuccess, isSmartRebalanceError, isSuccess, isError])

  useEffect(() => {
    if (eliteOnboardingData) {
      trackEvent({
        eventName: EventNames.ScreenLoaded,
        payload: {
        ...getEliteBuyCtaEventExtras(eliteOnboardingData ?? (data?.data as EliteOnboardingStatusResponse | undefined)),
          eventId: ELITE_LANDING_PAGE_VERSION,
          screenType: ELITE_LANDING_PAGE_VERSION,
          buttonLabel: `RB${eliteOnboardingData?.missedGains && eliteOnboardingData?.missedGains > 0 ? '_MG' : ''}`
        }
      })
    }
  }, [eliteOnboardingData])

  const onBGRiveLoad = () => { }

  const eliteLandingPageState = useMemo(() => {
    return eliteOnboardingData?.eliteLandingPageState ?? EliteLandingPageState.ELITE
  }, [eliteOnboardingData])

  const landingOffer = useMemo(
    () => resolveEliteLandingOfferFromStatus(eliteOnboardingData),
    [eliteOnboardingData]
  )

  useEffect(() => {
    setIsEliteButtonDisabled(
      () =>
        eliteLandingPageStateConfig?.[eliteLandingPageState]?.isDisabled ||
        !eliteOnboardingData?.cta ||
        eliteLandingPageState === EliteLandingPageState.FAMILY_ONBOARDING_NOT_ALLOWED
    )
  }, [eliteLandingPageState])

  useEffect(() => {
    // Initialize consent state - default to true if no consentText
    // EliteButtonV3 will update this via handleConsentStateChange when it loads
    if (!hasEliteConsentText(eliteOnboardingData?.consentTextList, eliteOnboardingData?.consentText)) {
      setIsConsentChecked(true)
    }
  }, [eliteOnboardingData?.consentText, eliteOnboardingData?.consentTextList])

  const handleConsentStateChange = (checked: boolean) => {
    setIsConsentChecked(checked)
  }

  if (isLoading) return <EliteLandingPageLoader />

  return (
    <EliteBuyCtaExtrasProvider status={eliteOnboardingData ?? (data?.data as EliteOnboardingStatusResponse | undefined)}>
    <div className={`h-screen flex flex-col relative overflow-hidden bg-[#010101] w-full`}>
      <BrokeLandingPageBackBar isEliteButtonVisible={isEliteButtonVisible} />

      <div
        ref={scrollRootRef}
        className='flex-1 min-h-0 w-full overflow-x-hidden overflow-y-auto overscroll-none no-scrollbar'
      >
        <div className='relative z-0 flex h-max w-full flex-col'>
          <BrokeEliteLandingPageHead
            handleEliteButtonVisibility={handleEliteButtonVisibility}
            isWhitelisted={isWhitelisted}
            isNotified={isNotified}
            handleIsNotified={handleIsNotified}
            eliteLandingPageState={eliteOnboardingData?.eliteLandingPageState ?? EliteLandingPageState.ELITE}
            amount={eliteOnboardingData?.userSubscriptionDetails?.membershipFees?.amount ?? 0}
            cta={eliteOnboardingData?.cta ?? null}
            isForceUpdateRequired={eliteOnboardingData?.isForceUpdateRequired ?? false}
            isEliteButtonDisabled={isEliteButtonDisabled}
            activationExpiryDate={eliteOnboardingData?.userSubscriptionDetails?.activationExpiryDate ?? undefined}
            onBGRiveLoad={onBGRiveLoad}
            version={ELITE_LANDING_PAGE_VERSION}
            isEliteButtonVisible={isEliteButtonVisible}
            consentText={eliteOnboardingData?.consentText}
            consentTextList={eliteOnboardingData?.consentTextList}
            familyMemberName={eliteOnboardingData?.secondaryUserName}
            onConsentStateChange={handleConsentStateChange}
            specialOffering={landingOffer.specialOffering}
            offerStartTime={landingOffer.offerStartTime}
            offerExpiryTime={landingOffer.offerExpiryTime}
            referralReward={eliteOnboardingData?.referralReward ?? null}
            offerType={landingOffer.offerType}
            signupOffer={landingOffer.signupOffer}
            inlineEliteCtaRef={inlineEliteCtaRef}
          />
          <div className='bg-[#FEFFFE] z-10'>
            <BrokeEliteLandingPageBody
              numberOfFundsToRebalance={eliteOnboardingData?.numberOfFundsToRebalance ?? 0}
              missedGains={eliteOnboardingData?.missedGains ?? 0}
              cta={eliteOnboardingData?.cta ?? null}
              timeToRebalancePlanDrop={smartRebalanceData?.data?.timeToRebalancePlanDrop}
              isEliteButtonDisabled={isEliteButtonDisabled}
              isConsentChecked={isConsentChecked}
              isForceUpdateRequired={eliteOnboardingData?.isForceUpdateRequired ?? false}
              consentText={eliteOnboardingData?.consentText}
              consentTextList={eliteOnboardingData?.consentTextList}
              screenType={ELITE_LANDING_PAGE_VERSION}
              amount={eliteOnboardingData?.userSubscriptionDetails?.membershipFees?.amount}
            />
          </div>
          {!isEliteButtonDisabled && isEliteButtonVisible ? (
            <div
              aria-hidden
              className={getEliteConsentScrollSpacerClass(
                eliteOnboardingData?.consentText,
                eliteOnboardingData?.consentTextList
              )}
            />
          ) : null}
        </div>
      </div>

      {!isEliteButtonDisabled && isEliteButtonVisible && (
        <motion.div
          ref={floatingEliteCtaRef}
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          transition={{ type: 'tween', stiffness: 300, damping: 25, duration: 0.25 }}
          style={{ bottom: `${getBottomPadding({ includeExtraPadding: false })}px` }}
          className={`pointer-events-auto fixed left-0 right-0 z-50 px-5 pt-5 pb-10 animate-fade-150 flex w-full 
            bg-gradient-to-b from-[rgba(255,255,255,0)] via-[rgba(255,255,255,0.3)] to-[rgba(255,255,255,0.8)] 
            ${hasEliteConsentText(eliteOnboardingData?.consentTextList, eliteOnboardingData?.consentText) ? '!bg-white !pt-5 !pb-5' : ''}`}
        >
          <EliteButtonV3
            eliteLandingPageState={eliteLandingPageState}
            amount={eliteOnboardingData?.userSubscriptionDetails?.membershipFees?.amount ?? 0}
            cta={eliteOnboardingData?.cta ?? null}
            isForceUpdateRequired={eliteOnboardingData?.isForceUpdateRequired ?? false}
            version={ELITE_LANDING_PAGE_VERSION}
            theme={'INVERTED_ELITE'}
            isEliteButtonVisible={isEliteButtonVisible}
            consentText={eliteOnboardingData?.consentText}
            consentTextList={eliteOnboardingData?.consentTextList}
            familyMemberName={eliteOnboardingData?.secondaryUserName}
            onConsentStateChange={handleConsentStateChange}
            referralReward={eliteOnboardingData?.referralReward ?? null}
          />
        </motion.div>
      )}
      <EliteAboutVideoFab
        showFab={showAboutVideoFab}
        isEliteCtaVisible={!isEliteButtonDisabled && isEliteButtonVisible}
        eliteCtaRef={floatingEliteCtaRef}
      />
    </div>
    </EliteBuyCtaExtrasProvider>
  )
}

export function BrokeLandingPageBackBar({
  isEliteButtonVisible
}: {
  isEliteButtonVisible: boolean
}) {
  return (
    <ElitePageBackBar isEliteButtonVisible={isEliteButtonVisible} />
  )
}
